import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

interface Execution {
  name: string;
  status: string;
  startDate?: string;
  stopDate?: string;
  executionArn: string;
}

interface RunsResponse {
  executions: Execution[];
  configured: boolean;
}

interface RunEvent {
  id: number;
  type: string;
  timestamp?: string;
  previousEventId?: number;
  stateName?: string;
  functionName?: string;
  resource?: string;
  error?: string;
  cause?:
    | string
    | { errorMessage?: string; errorType?: string; stackTrace?: string[] | string };
  output?: string;
  input?: string;
}

interface RunStep {
  ts?: string;
  kind: "info" | "success" | "warn" | "error";
  text: string;
}

interface RunSummary {
  emails_scanned?: number;
  emails_processed?: number;
  jobs_new?: number;
  jobs_retried?: number;
  jobs_total?: number;
  jobs_fetched_ok?: number;
  jobs_fetched_failed?: number;
  jobs_scored?: number;
  good_fits?: number;
  skipped_low_score?: number;
  prepared?: number;
  errors?: number;
}

interface RunDetail {
  name: string;
  status: string;
  startDate?: string;
  stopDate?: string;
  input?: string;
  output?: string;
  error?: string;
  cause?: string;
  events: RunEvent[];
  steps?: RunStep[];
  summary?: RunSummary | null;
}

const STATUS_COLOR: Record<string, string> = {
  RUNNING: "bg-amber-900/40 text-amber-200 border-amber-800",
  SUCCEEDED: "bg-emerald-900/40 text-emerald-200 border-emerald-800",
  FAILED: "bg-rose-900/40 text-rose-200 border-rose-800",
  TIMED_OUT: "bg-rose-900/40 text-rose-200 border-rose-800",
  ABORTED: "bg-slate-800 text-slate-300 border-slate-700",
};

export function Runs() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["runs"],
    queryFn: () => api<RunsResponse>("/runs", token),
    enabled: !!token,
    refetchInterval: (q) =>
      q.state.data?.executions.some((e) => e.status === "RUNNING") ? 3000 : false,
  });

  const detail = useQuery({
    queryKey: ["run", selected],
    queryFn: () => api<RunDetail>(`/runs/${selected}`, token),
    enabled: !!token && !!selected,
    refetchInterval: (q) => (q.state.data?.status === "RUNNING" ? 3000 : false),
  });

  const start = useMutation({
    mutationFn: () =>
      api<{ name: string }>("/runs", token, { method: "POST", body: JSON.stringify({}) }),
    onSuccess: (r) => {
      setSelected(r.name);
      qc.invalidateQueries({ queryKey: ["runs"] });
    },
  });

  const remove = useMutation({
    mutationFn: (name: string) =>
      api<{ deleted: string }>(`/runs/${name}`, token, { method: "DELETE" }),
    onSuccess: (_r, name) => {
      if (selected === name) setSelected(null);
      qc.invalidateQueries({ queryKey: ["runs"] });
    },
  });

  const clearAll = useMutation({
    mutationFn: () =>
      api<{ deleted: number }>("/runs", token, { method: "DELETE" }),
    onSuccess: () => {
      setSelected(null);
      qc.invalidateQueries({ queryKey: ["runs"] });
    },
  });

  const hasRunning = !!list.data?.executions.some((e) => e.status === "RUNNING");
  const hasTerminal = !!list.data?.executions.some((e) => e.status !== "RUNNING");

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-50">Runs</h1>
          <p className="mt-1 text-sm text-slate-400">
            Step Functions executions for the ingest → fetch → score pipeline.
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Delete only hides runs from this view — Step Functions retains the
            underlying execution for 90 days.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            className="rounded-md border border-slate-700 px-3 py-1.5 text-sm font-medium text-slate-200 transition hover:bg-slate-800 disabled:opacity-40"
            onClick={() => {
              if (window.confirm("Hide all completed runs from this list?")) {
                clearAll.mutate();
              }
            }}
            disabled={clearAll.isPending || hasRunning || !hasTerminal}
            title={hasRunning ? "Wait for the current run to finish" : "Hide all completed runs"}
          >
            {clearAll.isPending ? "Clearing…" : "Clear all"}
          </button>
          <button
            type="button"
            className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50"
            onClick={() => start.mutate()}
            disabled={start.isPending || !list.data?.configured}
          >
            {start.isPending ? "Starting…" : "Run pipeline now"}
          </button>
        </div>
      </header>

      {start.error && (
        <div className="rounded-md border border-rose-700 bg-rose-950/40 px-4 py-2 text-sm text-rose-200">
          {(start.error as Error).message}
        </div>
      )}

      {list.data && !list.data.configured && (
        <div className="rounded-xl border border-amber-800 bg-amber-950/30 px-5 py-4 text-sm text-amber-200">
          Pipeline not deployed yet. Save IMAP creds + LinkedIn cookie in Settings, then deploy
          the JobHunter-Pipeline stack.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
        <ExecutionList
          executions={list.data?.executions ?? []}
          loading={list.isPending}
          selected={selected}
          onSelect={setSelected}
          onDelete={(name) => remove.mutate(name)}
          deletingName={remove.isPending ? remove.variables ?? null : null}
        />
        <ExecutionDetail
          loading={detail.isFetching}
          data={detail.data}
          error={detail.error as Error | null}
        />
      </div>
    </div>
  );
}

function ExecutionList({
  executions,
  loading,
  selected,
  onSelect,
  onDelete,
  deletingName,
}: {
  executions: Execution[];
  loading: boolean;
  selected: string | null;
  onSelect: (name: string) => void;
  onDelete: (name: string) => void;
  deletingName: string | null;
}) {
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900">
      <div className="border-b border-slate-800 px-5 py-3 text-sm font-semibold uppercase tracking-wide text-slate-400">
        Recent
      </div>
      {loading && <p className="p-5 text-slate-400">Loading…</p>}
      {!loading && executions.length === 0 && (
        <p className="p-5 text-sm text-slate-500">No executions yet.</p>
      )}
      <ul className="divide-y divide-slate-800">
        {executions.map((e) => {
          const isTerminal = e.status !== "RUNNING";
          const isDeleting = deletingName === e.name;
          return (
            <li key={e.name} className="group relative">
              <button
                type="button"
                onClick={() => onSelect(e.name)}
                className={`w-full px-5 py-3 pr-12 text-left transition hover:bg-slate-800/60 ${
                  selected === e.name ? "bg-slate-800/80" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-xs text-slate-300">{e.name}</span>
                  <StatusBadge status={e.status} />
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {e.startDate ? new Date(e.startDate).toLocaleString() : "—"}
                  {e.stopDate && ` → ${new Date(e.stopDate).toLocaleTimeString()}`}
                </div>
              </button>
              {isTerminal && (
                <button
                  type="button"
                  aria-label={`Delete run ${e.name}`}
                  className="absolute right-2 top-2 rounded-md px-2 py-1 text-xs text-slate-500 opacity-0 transition hover:bg-slate-800 hover:text-rose-300 group-hover:opacity-100 disabled:opacity-40"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    if (window.confirm(`Delete run ${e.name}?`)) {
                      onDelete(e.name);
                    }
                  }}
                  disabled={isDeleting}
                  title="Delete this run"
                >
                  {isDeleting ? "…" : "×"}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ExecutionDetail({
  loading,
  data,
  error,
}: {
  loading: boolean;
  data?: RunDetail;
  error: Error | null;
}) {
  if (error) {
    return (
      <section className="rounded-xl border border-rose-800 bg-rose-950/30 p-5 text-sm text-rose-200">
        {error.message}
      </section>
    );
  }
  if (!data) {
    return (
      <section className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 p-10 text-center text-sm text-slate-500">
        Pick an execution to see its history.
      </section>
    );
  }
  return (
    <section className="space-y-4 rounded-xl border border-slate-800 bg-slate-900 p-5">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-xs text-slate-400">{data.name}</div>
          <div className="mt-1 text-sm text-slate-200">
            {data.startDate && new Date(data.startDate).toLocaleString()}
            {data.stopDate && ` → ${new Date(data.stopDate).toLocaleTimeString()}`}
          </div>
        </div>
        <StatusBadge status={data.status} />
        {loading && <span className="text-xs text-slate-500">refreshing…</span>}
      </header>

      {data.summary && <SummaryCard summary={data.summary} />}

      {(data.error || data.cause) && (
        <div className="rounded-md border border-rose-700 bg-rose-950/40 p-3 text-xs text-rose-200">
          {data.error && <div className="font-semibold">{data.error}</div>}
          {data.cause && <pre className="mt-1 whitespace-pre-wrap break-all">{pretty(data.cause)}</pre>}
        </div>
      )}

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">History</h3>
        {data.steps && data.steps.length > 0 ? (
          <ol className="mt-2 space-y-1.5">
            {data.steps.map((s, i) => <StepRow key={i} step={s} />)}
          </ol>
        ) : (
          <p className="mt-2 text-xs text-slate-500">No steps recorded yet…</p>
        )}
      </div>

      <details className="rounded-md bg-slate-950/60 p-3">
        <summary className="cursor-pointer text-xs uppercase tracking-wide text-slate-400">
          Raw events &amp; payloads
        </summary>
        <div className="mt-3 space-y-3">
          {data.input && (
            <details className="rounded-md bg-slate-950/60 p-3">
              <summary className="cursor-pointer text-xs uppercase tracking-wide text-slate-400">Input</summary>
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all text-xs text-slate-300">
                {pretty(data.input)}
              </pre>
            </details>
          )}
          {data.output && (
            <details className="rounded-md bg-slate-950/60 p-3">
              <summary className="cursor-pointer text-xs uppercase tracking-wide text-slate-400">Output</summary>
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all text-xs text-slate-300">
                {pretty(data.output)}
              </pre>
            </details>
          )}
          <ol className="space-y-1 font-mono text-xs">
            {data.events.map((ev) => <EventRow key={ev.id} ev={ev} />)}
          </ol>
        </div>
      </details>
    </section>
  );
}

const STEP_KIND_STYLES: Record<RunStep["kind"], { dot: string; text: string }> = {
  info: { dot: "bg-slate-500", text: "text-slate-200" },
  success: { dot: "bg-emerald-500", text: "text-emerald-100" },
  warn: { dot: "bg-amber-500", text: "text-amber-100" },
  error: { dot: "bg-rose-500", text: "text-rose-100" },
};

function StepRow({ step }: { step: RunStep }) {
  const style = STEP_KIND_STYLES[step.kind] ?? STEP_KIND_STYLES.info;
  return (
    <li className="flex items-start gap-3 text-sm">
      <span className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${style.dot}`} />
      <span className="w-20 shrink-0 text-xs text-slate-500">
        {step.ts ? shortTime(step.ts) : ""}
      </span>
      <span className={`flex-1 ${style.text}`}>{step.text}</span>
    </li>
  );
}

function SummaryCard({ summary }: { summary: RunSummary }) {
  const tiles: { label: string; value: number; tone?: "good" | "warn" | "error" }[] = [
    { label: "Emails scanned", value: summary.emails_scanned ?? 0 },
    { label: "Emails used", value: summary.emails_processed ?? 0 },
    { label: "Jobs seen", value: summary.jobs_total ?? 0 },
    { label: "Scored", value: summary.jobs_scored ?? 0 },
    { label: "Good fits", value: summary.good_fits ?? 0, tone: "good" },
    { label: "Prepared", value: summary.prepared ?? 0, tone: "good" },
    { label: "Skipped", value: summary.skipped_low_score ?? 0 },
    { label: "Fetch failed", value: summary.jobs_fetched_failed ?? 0, tone: "warn" },
    { label: "Errors", value: summary.errors ?? 0, tone: "error" },
  ];
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/60 p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
        Run summary
      </h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {tiles.map((t) => {
          const toneText =
            t.tone === "good" && t.value > 0
              ? "text-emerald-300"
              : t.tone === "warn" && t.value > 0
              ? "text-amber-300"
              : t.tone === "error" && t.value > 0
              ? "text-rose-300"
              : "text-slate-100";
          return (
            <div
              key={t.label}
              className="rounded-md border border-slate-800 bg-slate-900 px-3 py-2"
            >
              <div className="text-[11px] uppercase tracking-wide text-slate-500">{t.label}</div>
              <div className={`mt-0.5 text-xl font-semibold ${toneText}`}>{t.value}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventRow({ ev }: { ev: RunEvent }) {
  const isFail = ev.type.includes("Failed") || ev.type.includes("TimedOut");
  const cause =
    typeof ev.cause === "object" && ev.cause
      ? ev.cause.errorMessage || ""
      : typeof ev.cause === "string"
      ? ev.cause
      : "";
  const stack =
    typeof ev.cause === "object" && ev.cause && Array.isArray(ev.cause.stackTrace)
      ? ev.cause.stackTrace.join("")
      : typeof ev.cause === "object" && ev.cause && typeof ev.cause.stackTrace === "string"
      ? ev.cause.stackTrace
      : "";

  return (
    <li className={isFail ? "rounded bg-rose-950/40 px-1.5 py-1" : ""}>
      <div className="grid grid-cols-[3rem_minmax(11rem,auto)_1fr_auto] gap-2 text-slate-400">
        <span className="text-slate-600">#{ev.id}</span>
        <span className={isFail ? "text-rose-300" : "text-slate-300"}>{eventLabel(ev.type)}</span>
        <span className="truncate text-slate-200">
          {ev.stateName && <span className="text-slate-100">{ev.stateName}</span>}
          {ev.functionName && (
            <span className="ml-1 text-cyan-300">[{ev.functionName}]</span>
          )}
        </span>
        <span className="text-slate-500">{ev.timestamp && shortTime(ev.timestamp)}</span>
      </div>
      {(ev.error || cause) && (
        <div className="mt-1 ml-[3rem] border-l-2 border-rose-700 pl-2 text-rose-200">
          {ev.error && <div className="font-semibold">{ev.error}</div>}
          {cause && <div className="whitespace-pre-wrap break-all">{cause}</div>}
          {stack && (
            <details className="mt-1">
              <summary className="cursor-pointer text-rose-400">stack</summary>
              <pre className="mt-1 whitespace-pre-wrap text-rose-300/80">{stack}</pre>
            </details>
          )}
        </div>
      )}
      {ev.output && !isFail && (
        <details className="mt-1 ml-[3rem]">
          <summary className="cursor-pointer text-slate-500">output</summary>
          <pre className="mt-1 whitespace-pre-wrap break-all text-slate-300">{pretty(ev.output)}</pre>
        </details>
      )}
    </li>
  );
}

function shortTime(s: string): string {
  return new Date(s).toLocaleTimeString();
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
        STATUS_COLOR[status] ?? "bg-slate-800 text-slate-300 border-slate-700"
      }`}
    >
      {status.toLowerCase()}
    </span>
  );
}

function eventLabel(t: string): string {
  return t.replace(/([A-Z])/g, " $1").trim();
}

function pretty(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
